import { create } from "zustand";

/* ─────────────── Types ─────────────── */

export interface Hop {
  num: number;
  type: "origin" | "transit" | "ixp" | "cable" | "destination";
  city: string;
  country: string;
  lat: number;
  lng: number;
  ip: string;
  asn: string;
  asnName: string;
  latency: number; // per-hop ms
  cumLatency: number; // cumulative ms
  distance: number; // km from previous
  isIXP: boolean;
  ixpName: string | null;
  ixpTraffic: string | null;
  cable: {
    name: string;
    length: number;
    year: number;
    owners: string;
    capacity: string;
  } | null;
}

export interface CablePath {
  coords: number[][];
  name: string;
  color: string;
  featureId: string;
  // Glow layer support (generated at render time, not persisted)
  isGlow?: boolean;
  // Dash animation for active cables
  dashLength?: number;
  dashGap?: number;
  dashAnimateTime?: number;
}

export type TracePhase =
  | "idle"
  | "loading"
  | "animating"
  | "complete";

export type CameraMode =
  | "orbit"       // idle auto-rotate
  | "departure"   // zoom into origin
  | "follow"      // follow particle across continents
  | "whale"       // pull back for ocean crossing
  | "arrival"     // hold on destination
  | "reveal"      // the pullback reveal shot
  | "free";       // user-controlled

export type SpeedMode = "narrative" | "fast" | "realtime";

interface TraceState {
  // Phase
  phase: TracePhase;
  setPhase: (phase: TracePhase) => void;

  // Target URL
  targetUrl: string;
  setTargetUrl: (url: string) => void;

  // Hops data
  hops: Hop[];
  setHops: (hops: Hop[]) => void;

  // Active hop (for animation sequencing)
  activeHopIndex: number;
  setActiveHopIndex: (index: number) => void;

  // Camera mode
  cameraMode: CameraMode;
  setCameraMode: (mode: CameraMode) => void;

  // Speed
  speedMode: SpeedMode;
  setSpeedMode: (mode: SpeedMode) => void;

  // Sidebar
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;

  // Layer visibility
  layers: {
    cables: boolean;
    infra: boolean;
    arcs: boolean;
    events: boolean;
  };
  toggleLayer: (layer: keyof TraceState["layers"]) => void;

  // Probe origin (where the traceroute was executed from)
  probe: { city: string; country: string; continent: string } | null;
  setProbe: (probe: TraceState["probe"]) => void;

  // Cable data
  cables: CablePath[];
  setCables: (cables: CablePath[]) => void;
  activeCableIds: string[];
  setActiveCableIds: (ids: string[]) => void;

  // Reset
  reset: () => void;
}

const initialState = {
  phase: "idle" as TracePhase,
  targetUrl: "",
  hops: [] as Hop[],
  activeHopIndex: -1,
  cameraMode: "orbit" as CameraMode,
  speedMode: "narrative" as SpeedMode,
  sidebarOpen: true,
  layers: { cables: true, infra: true, arcs: true, events: false },
  probe: null as { city: string; country: string; continent: string } | null,
  cables: [] as CablePath[],
  activeCableIds: [] as string[],
};

export const useTraceStore = create<TraceState>((set) => ({
  ...initialState,

  setPhase: (phase) => set({ phase }),
  setTargetUrl: (targetUrl) => set({ targetUrl }),
  setHops: (hops) => set({ hops }),
  setActiveHopIndex: (activeHopIndex) => set({ activeHopIndex }),
  setCameraMode: (cameraMode) => set({ cameraMode }),
  setSpeedMode: (speedMode) => set({ speedMode }),
  setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
  toggleLayer: (layer) =>
    set((state) => ({
      layers: { ...state.layers, [layer]: !state.layers[layer] },
    })),
  setProbe: (probe) => set({ probe }),
  setCables: (cables) => set({ cables }),
  setActiveCableIds: (activeCableIds) => set({ activeCableIds }),
  reset: () => set(initialState),
}));
